import { commonUtils } from '@/helpers/commonUtils'
import { buyVipService } from '@/services/buyVipService'
import { FeatureType, userService } from '@/services/userService'
import { Modal, QRCode, Spin } from 'antd'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef, useState } from 'react'
import DiscountCodeInput from '../DiscountCodeInput/DiscountCodeInput'
import styles from './FeaturePayDialog.module.less'

// 价格相关常量
const PRICE_UNITS = {
  YEAR: '年',
  MONTH: '月',
} as const

const DISCOUNT_THRESHOLD = {
  MIN: 0,
  MAX: 10,
} as const

// 价格计算工具函数
const getPriceUnit = (timeType: string | undefined): string => {
  return timeType === 'year' ? PRICE_UNITS.YEAR : PRICE_UNITS.MONTH
}

const isYearlyPlan = (timeType: string | undefined): boolean => {
  return timeType === 'year'
}

const shouldShowDiscount = (discountPercent: number | undefined): boolean => {
  return Boolean(
    discountPercent &&
      discountPercent > DISCOUNT_THRESHOLD.MIN &&
      discountPercent < DISCOUNT_THRESHOLD.MAX
  )
}

const getMainPrice = (plan: ServerDataTypes.FormatPlan | null): number | undefined => {
  if (!plan) return undefined
  return isYearlyPlan(plan.timeType) ? plan.price : plan.unitPrice
}

const getMonthlyPrice = (plan: ServerDataTypes.FormatPlan | null): number | undefined => {
  if (!plan) return undefined
  return isYearlyPlan(plan.timeType) ? plan.unitPrice : plan.price
}

const getOriginalPrice = (plan: ServerDataTypes.FormatPlan | null): number | undefined => {
  if (!plan) return undefined
  return isYearlyPlan(plan.timeType) ? plan.originPrice : plan.unitPrice
}

export interface FeaturePayDialogProps {
  /** 显示状态（必需） */
  open: boolean
  /** 关闭回调（必需） */
  onClose: () => void
  /** 自定义样式类名 */
  className?: string
  /** 会员类型 */
  vipType: ServerDataTypes.VersionType
  feature: FeatureType
  source: string
  from?: string
  enableDiscountCode?: boolean
  paySuccess?: (enableReceiveGift: boolean) => void
}

export const FeaturePayDialog = observer((props: FeaturePayDialogProps) => {
  const {
    open,
    onClose,
    className,
    vipType = 'max',
    feature = 'other',
    source,
    enableDiscountCode = false,
    paySuccess,
    from,
  } = props

  const [selectedPlan, setSelectedPlan] = useState<ServerDataTypes.FormatPlan | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<'wx_pub_qr' | 'alipay_qr'>('alipay_qr')
  const [yearPlan, setYearPlan] = useState<ServerDataTypes.FormatPlan | null>(null)
  const [monthPlan, setMonthPlan] = useState<ServerDataTypes.FormatPlan | null>(null)

  const timerId = useRef<NodeJS.Timeout | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(false)
  const [discountCodeData, setDiscountCodeData] = useState<{
    discountCode: string
    statusInfo: {
      status: 'none' | 'success' | 'error'
      msg: string
    }
  }>({
    discountCode: '',
    statusInfo: {
      status: 'none',
      msg: '',
    },
  })

  // 折扣
  const [discountCodePercent, setDiscountCodePercent] = useState<number | null>(null)

  const priceInfo = useMemo(() => {
    if (!selectedPlan) {
      return {
        mainPrice: undefined,
        priceUnit: PRICE_UNITS.MONTH,
        monthlyPrice: undefined,
        originalPrice: undefined,
        showDiscount: false,
        discountPercent: undefined,
        isYearly: false,
      }
    }

    // 如果有折扣价格，使用折扣价格；否则使用原价格
    let mainPrice = getMainPrice(selectedPlan)!
    let monthlyPrice = getMonthlyPrice(selectedPlan)!
    if (discountCodePercent && selectedPlan.discountPrice) {
      mainPrice = selectedPlan.discountPrice
      monthlyPrice = commonUtils.calculateDiscountPrice(monthlyPrice, discountCodePercent)
    }

    return {
      mainPrice: mainPrice,
      priceUnit: getPriceUnit(selectedPlan.timeType),
      monthlyPrice: monthlyPrice,
      originalPrice: getOriginalPrice(selectedPlan),
      showDiscount: shouldShowDiscount(selectedPlan.discountPercent),
      discountPercent: selectedPlan.discountPercent,
      isYearly: isYearlyPlan(selectedPlan.timeType),
    }
  }, [selectedPlan, discountCodePercent])

  const [config, setConfig] = useState<{
    title: string
    subtitle: string
    features: { name: string; value: React.ReactNode; active?: boolean }[]
    bgClass: string
    qrCodeBg: string
    qrUrl: string
    qrErr: string
  }>({
    title: '',
    subtitle: '',
    features: [],
    bgClass: '',
    qrCodeBg: '',
    qrUrl: '',
    qrErr: '',
  })

  const genOrderQrCode = async (
    plan: ServerDataTypes.FormatPlan,
    currentTabKey: 'wx_pub_qr' | 'alipay_qr',
    source: string,
    discountCode?: string
  ) => {
    if (loading) {
      return
    }

    if (!plan) {
      return
    }

    const isDiscountCode = discountCode !== undefined && discountCode

    const selectPlan = plan
    setSelectedPlan(plan)

    // 常规模式：清空二维码状态
    if (!isDiscountCode) {
      clearTimeout(timerId.current)
      // 清除折扣价格，还原原价
      setDiscountCodePercent(null)
      setConfig((prev) => ({
        ...prev,
        qrErr: '',
        qrUrl: '',
      }))
    }

    setLoading(true)
    try {
      const { qr, orderId, money } = await buyVipService.createOrder({
        action: userService.userData?.versionData.version === 'free' ? 'buy' : 'renew',
        // chargeChannel: this.currentTabKey,
        chargeChannel:
          selectPlan.isSubscription && currentTabKey === 'alipay_qr'
            ? 'alipay_agreement_qr'
            : currentTabKey,
        planId: selectPlan.id,
        version: selectPlan.version,
        source: source,
        ...(isDiscountCode && { discountTicket: discountCode }),
      })

      setConfig((prev) => ({
        ...prev,
        qrUrl: qr,
        qrErr: '',
      }))

      // 折扣码模式：更新成功状态
      if (isDiscountCode) {
        // 设置折扣码百分比
        const discountPricePercent = Number(money / selectPlan.price)
        selectPlan.discountPrice = money / 100
        setDiscountCodePercent(discountPricePercent)
        setDiscountCodeData((prev) => ({
          ...prev,
          statusInfo: {
            status: 'success',
            msg: '兑换成功',
          },
        }))
        clearTimeout(timerId.current)
      }

      pollingOrderStatus(orderId, true)
    } catch (error) {
      console.log(error)

      // 根据模式选择不同的错误处理
      if (isDiscountCode) {
        // 折扣码模式：更新 statusInfo
        setDiscountCodeData((prev) => ({
          ...prev,
          statusInfo: {
            status: 'error',
            msg: commonUtils.formatErrorMsg(error, '兑换码无效'),
          },
        }))
      } else {
        // 常规模式：更新 qrErr
        setConfig((prev) => ({
          ...prev,
          qrErr: commonUtils.formatErrorMsg(error, '生成二维码失败'),
        }))
      }
    } finally {
      setLoading(false)
    }
  }

  const pollingOrderStatus = async (orderId: string, force = false, cnt = 0) => {
    if (force) {
      try {
        const { status } = await buyVipService.pollOrderStatus(orderId)
        if (status === 'completed') {
          try {
            commonUtils.sendExtensionMessage('markPaid', {})
            userService.updateUserInfo()
          } catch (error) {}
          if (paySuccess) {
            paySuccess(selectedPlan?.timeType === 'year')
          }
          setTimeout(() => {
            if (onClose) {
              onClose()
            }
          })
          /** 停止轮询,给出成功提示 */
          clearTimeout(timerId.current)
          return
        }
        if (status === 'canceled' || status === 'refunded') {
          /** 停止轮询 */
          return
        }
        if (status === 'created') {
          /** 取消上一次轮询 */
          clearTimeout(timerId.current)
          timerId.current = setTimeout(() => {
            pollingOrderStatus(orderId, true, 0)
          }, 1000)
        }
      } catch (error) {
        if (cnt > 5) {
          clearTimeout(timerId.current)
          setConfig((prev) => ({
            ...prev,
            qrErr: commonUtils.formatErrorMsg(error, '查询二维码状态失败'),
          }))
          /** 重试结束，停止轮询 */
          return
        }
        pollingOrderStatus(orderId, true, cnt + 1)
      }
    }
  }

  useEffect(() => {
    const init = async () => {
      if (from === 'pricingPage') {
        userService.postHuiyiRecord({
          carrier: 'web-定价页',
          module: '定价页',
          function: `${vipType.charAt(0).toUpperCase() + vipType.slice(1)}付费弹窗2.0`,
          position: `定价页-${vipType.charAt(0).toUpperCase() + vipType.slice(1)}付费弹窗2.0`,
          action: '展现',
          element: '弹窗',
          remark: {
            feature,
          },
        })
      }
      const { maxPlanList, ultraPlanList } = await buyVipService.list()

      let yearPlan: ServerDataTypes.FormatPlan | null = null
      let monthPlan: ServerDataTypes.FormatPlan | null = null
      if (vipType === 'ultra') {
        const yearUltraPlan =
          ultraPlanList.find((item) => item.timeType === 'year') || ultraPlanList[0]
        const monthUltraPlan =
          ultraPlanList.find((item) => item.timeType === 'month_subscription') || ultraPlanList[0]
        yearPlan = commonUtils.formatDiscountPlan(yearUltraPlan)
        monthPlan = commonUtils.formatDiscountPlan(monthUltraPlan)
      } else {
        const yearMaxPlan = maxPlanList.find((item) => item.timeType === 'year') || maxPlanList[0]
        const monthMaxPlan =
          maxPlanList.find(
            (item) => item.timeType === 'month_subscription' || item.timeType === 'month'
          ) || maxPlanList[0]
        yearPlan = commonUtils.formatDiscountPlan(yearMaxPlan)
        monthPlan = commonUtils.formatDiscountPlan(monthMaxPlan)
      }

      setYearPlan(yearPlan)
      setMonthPlan(monthPlan)

      let vipColor = vipType === 'ultra' ? '#FF8A21' : '#832fff'
      let activeFeature = null
      // PDF 分段翻译
      if (feature === 'pdfPro') {
        const pdfProValue = (
          <>
            <p style={{ color: vipColor, fontWeight: 'bold' }}>
              {vipType === 'ultra' ? '3,000页/月' : '1,000页/月'}
            </p>
            <p>
              最大文件大小{' '}
              <span style={{ color: vipColor, fontWeight: 'bold' }}>
                {vipType === 'ultra' ? '1,500页/300M' : '500页/100M'}
              </span>
            </p>
          </>
        )
        activeFeature = { name: 'PDF 分段翻译', value: pdfProValue, active: true }
      }
      // PDF 无损翻译
      if (feature === 'pdfUltra') {
        const pdfUltraValue = (
          <>
            <p style={{ color: vipColor, fontWeight: 'bold' }}>
              {vipType === 'ultra' ? '15,000页/月' : '5,000页/月'}
            </p>
            <p>
              最大文件大小{' '}
              <span style={{ color: vipColor, fontWeight: 'bold' }}>
                {vipType === 'ultra' ? '1,500页/300M' : '500页/100M'}
              </span>
            </p>
          </>
        )
        activeFeature = { name: 'PDF 无损翻译', value: pdfUltraValue, active: true }
      }
      // 图片翻译
      if (feature === 'screenshot' || feature === 'image') {
        activeFeature = {
          name: '图片翻译',
          value: (
            <span style={{ color: vipColor, fontWeight: 'bold' }}>
              {vipType === 'ultra' ? '2,000张/月' : '1,000张/月'}
            </span>
          ),
          active: true,
        }
      }
      // Word 翻译
      if (feature === 'word') {
        const wordValue = (
          <>
            <p style={{ color: vipColor, fontWeight: 'bold' }}>不限页数</p>
            <p>
              最大文件大小{' '}
              <span style={{ color: vipColor, fontWeight: 'bold' }}>
                {vipType === 'ultra' ? '300M' : '50M'}
              </span>
            </p>
          </>
        )
        activeFeature = { name: 'Word翻译', value: wordValue, active: true }
      }
      // 智能语境
      if (feature === 'context') {
        const contextValue = (
          <>
            {vipType === 'ultra' ? (
              <p>
                <span style={{ color: vipColor, fontWeight: 'bold' }}>深度解析</span>
                ｜高级解析｜基础解析
              </p>
            ) : (
              <p>
                <span style={{ color: vipColor, fontWeight: 'bold' }}>高级解析</span>｜基础解析
              </p>
            )}
          </>
        )
        activeFeature = { name: '智能语境', value: contextValue, active: true }
      }
      // 术语表
      if (feature === 'glossary') {
        const glossaryValue = (
          <>
            {vipType === 'ultra' ? (
              <p>
                <span style={{ color: vipColor, fontWeight: 'bold' }}>10,000 </span>条自定义术语
              </p>
            ) : (
              <p>
                <span style={{ color: vipColor, fontWeight: 'bold' }}>200 </span>条自定义术语
              </p>
            )}
          </>
        )
        activeFeature = { name: '自定义术语表', value: glossaryValue, active: true }
      }
      // 翻译线路
      if (feature === 'translateSpeed') {
        const translateSpeedValue = (
          <>
            {vipType === 'ultra' ? (
              <p>
                <span style={{ color: vipColor, fontWeight: 'bold' }}>优质线路</span>
              </p>
            ) : (
              <p>
                <span style={{ color: vipColor, fontWeight: 'bold' }}>高级线路</span>
              </p>
            )}
          </>
        )
        activeFeature = { name: '翻译线路', value: translateSpeedValue, active: true }
      }
      // 文件解析速度
      if (feature === 'parseSpeed') {
        const parseSpeedValue = (
          <>
            {vipType === 'ultra' ? (
              <p>
                极速解析｜<span style={{ color: vipColor, fontWeight: 'bold' }}>20页/分钟</span>
              </p>
            ) : (
              <p>
                高速解析｜<span style={{ color: vipColor, fontWeight: 'bold' }}>10页/分钟</span>
              </p>
            )}
          </>
        )
        activeFeature = { name: '文件解析速度', value: parseSpeedValue, active: true }
      }
      // 文件保留时长
      if (feature === 'fileSaveTime') {
        const fileSaveTimeValue = (
          <>
            {vipType === 'ultra' ? (
              <p>
                文件及记录保留 <span style={{ color: vipColor, fontWeight: 'bold' }}>6个月</span>
              </p>
            ) : (
              <p>
                文件及记录保留 <span style={{ color: vipColor, fontWeight: 'bold' }}>1个月</span>
              </p>
            )}
          </>
        )
        activeFeature = { name: '文件保留时长', value: fileSaveTimeValue, active: true }
      }
      // 文件批量翻译
      if (feature === 'fileBatchTranslate') {
        const fileBatchTranslateValue = (
          <>
            {vipType === 'ultra' ? (
              <p>
                可批量翻译 <span style={{ color: vipColor, fontWeight: 'bold' }}>100 个文件</span>
              </p>
            ) : (
              <p>
                可批量翻译 <span style={{ color: vipColor, fontWeight: 'bold' }}>30 个文件</span>
              </p>
            )}
          </>
        )
        activeFeature = { name: '文件批量翻译', value: fileBatchTranslateValue, active: true }
      }
      if (feature === 'learn') {
        activeFeature = {
          name: '学习模式',
          value: <span style={{ fontWeight: 'bold' }}>无限制使用</span>,
          active: true,
        }
      }
      // Ultra 会员
      if (vipType === 'ultra') {
        setConfig({
          title: '沉浸式 AI 翻译-Ultra会员',
          subtitle: '🙋顶级模型、顶级额度、顶级权益、顶级体验！',
          features: [
            { name: '顶级模型', value: <span style={{ fontWeight: 'bold' }}>200 万/月</span> },
            { name: '高级模型', value: <span style={{ fontWeight: 'bold' }}>5,000 万/月</span> },
            ...(activeFeature ? [activeFeature] : []),
            { name: '更多权益...', value: '' },
          ],
          bgClass: styles.ultraTheme,
          qrCodeBg: commonUtils.getStaticFile('new_ultra_qr_border.png'),
          qrUrl: '',
          qrErr: '',
        })
      }
      // Max 会员
      if (vipType === 'max') {
        setConfig({
          title: '沉浸式 AI 翻译-Max会员',
          subtitle: '✨高级模型搭配充足额度，有效提高工作效率',
          features: [
            { name: '高级模型', value: <span style={{ fontWeight: 'bold' }}>2,000 万/月</span> },
            ...(activeFeature ? [activeFeature] : []),
            { name: '更多权益...', value: '' },
          ],
          bgClass: styles.maxTheme,
          qrCodeBg: commonUtils.getStaticFile('new_max_qr_border.png'),
          qrUrl: '',
          qrErr: '',
        })
      }

      genOrderQrCode(yearPlan, 'alipay_qr', source)
    }

    init()

    return () => {
      clearTimeout(timerId.current)
    }
  }, [])

  let vipTypeStr = 'Ultra版'
  let timeStr = '年度'
  if (vipType === 'max') {
    vipTypeStr = 'Max版'
  }
  if (selectedPlan?.timeType === 'month' || selectedPlan?.timeType === 'month_subscription') {
    timeStr = '月度'
  }
  const vipTitle = `${vipTypeStr}-${timeStr}会员`

  return (
    <Modal
      open={open}
      wrapClassName={classNames(styles.featurePayDialog, className)}
      centered
      footer={false}
      closable={true}
      maskClosable={false}
      keyboard={false}
      width={664}
      zIndex={99999}
      onCancel={onClose}
      getContainer={false}
    >
      <div className={classNames(styles.dialogContent, config.bgClass)}>
        {/* Header 头部区域 */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.title}>{config.title}</div>
            <div className={styles.subtitle}>{config.subtitle}</div>
          </div>
        </div>

        {/* Main Content 主要内容区域 */}
        <div className={styles.mainContent}>
          {/* 左侧：支付二维码区域 */}
          <div className={styles.leftPanel}>
            <div className={styles.qrCodeSection}>
              <div className={styles.qrCodeContainer}>
                {config.qrCodeBg && (
                  <img className={classNames(styles.qrCodeBg)} src={config.qrCodeBg} />
                )}

                {config.qrErr && (
                  <div
                    className={classNames(styles.reloadBox)}
                    onClick={() => {
                      genOrderQrCode(
                        selectedPlan!,
                        paymentMethod,
                        source,
                        discountCodeData.discountCode
                      )
                    }}
                  >
                    <img
                      className={classNames(styles.icon)}
                      src='https://assets.weibanzhushou.com/web/pt-web/icon__qr-reload.b68e5cd3c901.png'
                    />
                    <span>刷新</span>
                  </div>
                )}

                {!config.qrErr && (
                  <Spin spinning={!config.qrUrl || loading} size='small'>
                    {config.qrUrl && (
                      <QRCode
                        value={config.qrUrl}
                        size={180}
                        bordered={false}
                        style={{ padding: 0 }}
                      ></QRCode>
                    )}
                  </Spin>
                )}
              </div>

              {/* 支付方式选择 */}
              <div className={styles.paymentMethods}>
                <div
                  className={classNames(styles.paymentMethod, {
                    [styles.active]: paymentMethod === 'alipay_qr',
                  })}
                  onClick={() => {
                    setPaymentMethod('alipay_qr')
                    genOrderQrCode(
                      selectedPlan!,
                      'alipay_qr',
                      source,
                      discountCodeData.discountCode
                    )
                  }}
                >
                  <img
                    className={styles.paymentIcon}
                    src='https://assets.weibanzhushou.com/web/pt-web/ali-pay.18b4779d8424.png'
                  />
                  <span>支付宝支付</span>
                </div>
                <div
                  className={classNames(styles.paymentMethod, {
                    [styles.active]: paymentMethod === 'wx_pub_qr',
                  })}
                  onClick={() => {
                    setPaymentMethod('wx_pub_qr')
                    genOrderQrCode(
                      selectedPlan!,
                      'wx_pub_qr',
                      source,
                      discountCodeData.discountCode
                    )
                  }}
                >
                  <img
                    className={styles.paymentIcon}
                    src='https://assets.weibanzhushou.com/web/pt-web/wechat-pay.9115a8ba339f.png'
                  />
                  <span>微信支付</span>
                </div>
              </div>
            </div>
          </div>

          {/* 右侧：会员信息和价格区域 */}
          <div className={styles.rightPanel}>
            {/* 会员类型切换 */}
            <div className={styles.memberTabs}>
              <div
                className={classNames(styles.memberTab, {
                  [styles.active]: selectedPlan?.timeType === 'year',
                })}
                onClick={() => {
                  /* 切换到Year */

                  genOrderQrCode(yearPlan!, paymentMethod, source, discountCodeData.discountCode)
                }}
              >
                <span>年度订阅</span>
                {yearPlan?.percentOff && yearPlan?.percentOff > 0 && yearPlan?.percentOff < 100 && (
                  <div className={styles.discountTag}>立省{yearPlan?.percentOff}%</div>
                )}
              </div>
              <div
                className={classNames(styles.memberTab, {
                  [styles.active]:
                    selectedPlan?.timeType === 'month' ||
                    selectedPlan?.timeType === 'month_subscription',
                })}
                onClick={() => {
                  /* 切换到Month */
                  genOrderQrCode(monthPlan!, paymentMethod, source, discountCodeData.discountCode)
                }}
              >
                <span>月度订阅</span>
              </div>
            </div>

            {/* 会员权益展示 */}
            <div className={styles.memberBenefits}>
              <div className={styles.memberTitle}>
                <img
                  className={classNames(styles.memberTitleIcon)}
                  src={
                    vipType === 'ultra'
                      ? commonUtils.getStaticFile('price_ultra.png')
                      : commonUtils.getStaticFile('price_max.png')
                  }
                />
                {vipTitle}
              </div>
              <div className={styles.benefitsList}>
                {config.features.map((feature, index) => (
                  <div
                    key={index}
                    className={classNames(styles.benefitItem, { [styles.active]: feature.active })}
                  >
                    <div className={classNames(styles.left)}>
                      <img
                        className={styles.benefitIcon}
                        src={commonUtils.getStaticFile('price-desc-item-arrow.png')}
                      ></img>

                      <span className={styles.benefitName}>{feature.name}</span>
                    </div>

                    <span className={styles.benefitValue}>{feature.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 折扣码 */}
            {enableDiscountCode && (
              <DiscountCodeInput
                rootClassName={classNames(styles.discountCode, {
                  [styles.ultra]: vipType === 'ultra',
                })}
                discountCode={discountCodeData.discountCode}
                handleDiscountCodeChange={(value: string) => {
                  setDiscountCodeData((prev) => ({
                    discountCode: value,
                    statusInfo: {
                      status: 'none',
                      msg: '',
                    },
                  }))
                }}
                handleDiscountCodeClick={() => {
                  genOrderQrCode(
                    selectedPlan!,
                    paymentMethod,
                    source,
                    discountCodeData.discountCode
                  )
                }}
                statusInfo={discountCodeData.statusInfo}
              />
            )}

            {/* 价格信息 */}
            <div className={styles.pricingInfo}>
              <div className={styles.priceDisplay}>
                <span className={styles.mainPrice}>
                  {priceInfo.mainPrice}
                  <span className={styles.priceUnit}>元/{priceInfo.priceUnit}</span>
                </span>
                {priceInfo.showDiscount && (
                  <div className={styles.discountBadge}>立享{priceInfo.discountPercent}折优惠</div>
                )}
              </div>

              <div className={styles.priceDetails}>
                {priceInfo.isYearly && (
                  <>
                    <span className={styles.monthlyPrice}>
                      {priceInfo.monthlyPrice}元/{PRICE_UNITS.MONTH}
                    </span>
                  </>
                )}

                {priceInfo.originalPrice !== priceInfo.mainPrice && (
                  <span className={styles.originalPrice}>
                    原价{priceInfo.originalPrice}元/{priceInfo.priceUnit}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer 底部区域 */}
        <div className={styles.footer}>
          <div className={styles.footerText}>发票支持：2 个工作日内开出</div>
        </div>
      </div>
    </Modal>
  )
})
